import React from 'react';
import { Quote, Star } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    content: "NEURA AI transformed our online presence. We went from an outdated website to a modern, mobile-friendly site with online booking. Our appointment requests have tripled!",
    name: "Sarah Mitchell",
    role: "Owner, Mitchell Physio Clinic",
    avatar: "SM",
    metric: "3x more bookings"
  },
  {
    id: 2,
    content: "The e-commerce store they built is fantastic. Easy to manage, looks professional, and the SEO work means we're now on page 1 of Google for our main keywords.",
    name: "David Chen",
    role: "Founder, Chen's Home Decor",
    avatar: "DC",
    metric: "Page 1 on Google"
  },
  {
    id: 3,
    content: "Best decision we made was getting the AI chatbot. It handles enquiries 24/7, qualifies leads, and even books consultations. It's like having an extra staff member.",
    name: "Emma Roberts",
    role: "Director, Roberts Legal Services",
    avatar: "ER",
    metric: "24/7 lead capture"
  }
];

const TestimonialsSection = () => {
  return (
    <section className="relative py-24 bg-gray-50" data-testid="testimonials-section">
      {/* Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 180, 216, 0.08) 1px, transparent 1px),
                           linear-gradient(to bottom, rgba(0, 180, 216, 0.08) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-cyan-600 text-sm uppercase tracking-[0.3em] font-medium">Testimonials</span>
          <h2 className="text-4xl md:text-5xl font-extralight text-gray-900 mt-4 mb-6">
            What Our Clients
            <span className="font-normal text-cyan-600"> Are Saying</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Don't just take our word for it—hear from businesses we've helped grow online.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="group relative"
              data-testid={`testimonial-card-${testimonial.id}`}
            >
              {/* Card */}
              <div className="relative p-8 rounded-2xl border border-gray-200 bg-white hover:border-cyan-300 transition-all duration-500 h-full flex flex-col shadow-sm hover:shadow-lg">
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>

                {/* Quote Icon */}
                <Quote className="w-8 h-8 text-cyan-200 mb-4" />

                {/* Content */}
                <p className="text-gray-600 leading-relaxed flex-1 mb-6">
                  "{testimonial.content}"
                </p>

                {/* Metric Badge */}
                <div className="mb-6">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                    {testimonial.metric}
                  </span>
                </div>

                {/* Author */}
                <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-medium">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="text-gray-900 font-medium">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 text-sm mb-6">Technologies & platforms we work with</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="text-xl font-bold text-gray-400">WordPress</div>
            <div className="text-xl font-bold text-gray-400">Shopify</div>
            <div className="text-xl font-bold text-gray-400">Stripe</div>
            <div className="text-xl font-bold text-gray-400">HubSpot</div>
            <div className="text-xl font-bold text-gray-400">Google</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
